<template>
  <ak-sidebar-layout size="large" class="bg-grey-lightest">
    <template #sidebar>
      <ak-box class="h-full is-scrollable pb-huge">
        <ak-form>
          <ak-field label="Form" required>
            <template #helper>
              The form to show up on the popout/modal.
            </template>
            <ak-select
              v-model="formId"
              :options="forms"
              track-by="_id"
              label="title"
              required
              placeholder="Select option"
              @select="updateConfig"
            />
          </ak-field>

          <template v-if="formId">
            <ak-field label="Type" required>
              <ak-select
                v-model="type"
                :options="types"
                required
                track-by="value"
                label="label"
                @select="updateConfig"
              />
            </ak-field>

            <template v-if="type && type.value === 'popout'">
              <ak-field label="Popout Location" required>
                <ak-select
                  v-model="popoutLocation"
                  :options="locations"
                  required
                  track-by="value"
                  label="label"
                  @select="updateConfig"
                />
              </ak-field>

              <ak-field :label="`Popout ${$t('Colour')}`" required>
                <ak-input
                  v-model="buttonColour"
                  required
                  @blur="updateConfig"
                />
              </ak-field>

              <ak-field label="Popout Label" required>
                <ak-input v-model="buttonLabel" required @blur="updateConfig" />
              </ak-field>

              <ak-field :label="`Popout Font ${$t('Colour')}`" required>
                <ak-input
                  v-model="buttonFontColour"
                  required
                  @blur="updateConfig"
                />
              </ak-field>
            </template>

            <template v-if="type && type.value === 'modal'">
              <ak-field label="Trigger" required>
                <template #helper> When to display the modal. </template>
                <ak-select
                  v-model="modalTrigger"
                  :options="['abandonment', 'pageLoad', 'triggerDelay']"
                  required
                  @select="updateConfig"
                />
              </ak-field>

              <template v-if="modalTrigger === 'triggerDelay'">
                <ak-field label="Trigger Delay" required>
                  <template #helper>
                    Delay in ms for the modal to show up.
                  </template>
                  <ak-input
                    v-model="modalTriggerDelay"
                    required
                    type="number"
                    min="0"
                    @blur="updateConfig"
                  />
                </ak-field>
              </template>
            </template>

            <ak-field label="Content Header" required>
              <ak-input v-model="bodyHeader" required @blur="updateConfig" />
            </ak-field>

            <ak-field :label="`Content Header ${$t('Colour')}`" required>
              <ak-input
                v-model="bodyHeaderColour"
                required
                @blur="updateConfig"
              />
            </ak-field>

            <ak-field label="Content Text" required>
              <ak-input
                v-model="bodyText"
                type="textarea"
                required
                @blur="updateConfig"
              />
            </ak-field>

            <ak-field label="Content Image" addons required>
              <ak-input v-model="bodyImg" required />
              <ak-button id="changePopoutImage" @click="changeImage">
                <ak-icon type="image" />
              </ak-button>
            </ak-field>

            <ak-field label="Height From Top" required>
              <template #helper>
                Position of the modal from the top down.
              </template>
              <ak-input v-model="heightFromTop" required @blur="updateConfig" />
            </ak-field>

            <!-- <ak-field label="Popout Height" required>
              <template #helper> Sets default size of popout </template>
              <ak-input v-model="defaultHeight" required @blur="updateConfig" />
            </ak-field> -->

            <!-- <ak-field label="Popout Position" required>
              <template #helper>
                Scroll will follow users scroll, fixed will stay in the same
                location.
              </template>
              <ak-select
                v-model="popoutPosition"
                :options="['scroll', 'fixed']"
                required
                @select="updateConfig"
              />
            </ak-field> -->

            <ak-field label="Popout ID">
              <template #helper>
                Unique identifier to be used in custom styling.
              </template>
              <ak-input v-model="akeroPopoutId" disabled />
            </ak-field>
          </template>
        </ak-form>
      </ak-box>
    </template>
    <template #body>
      <ak-navbar
        v-if="formId"
        class="bg-grey-lightest bb"
        size="tiny"
        spacing="tiny"
      >
        <ak-navbar-item grow />
        <ak-navbar-item>
          <ak-button color="success" @click.prevent="exportPopout(config)">
            Export Popout
          </ak-button>
        </ak-navbar-item>
      </ak-navbar>
      <ak-popout-editor-preview v-model="config" />
    </template>
  </ak-sidebar-layout>
</template>

<script>
import SidebarLayout from '@akerolabs/akero-vue/src/components/sidebar/SidebarLayout.vue'
import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import Form from '@akerolabs/akero-vue/src/components/form/Form.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import Select from '@akerolabs/akero-vue/src/components/select/Select.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import NavbarButton from '@akerolabs/akero-vue/src/components/navbar/NavbarButton.vue'
import NavbarItem from '@akerolabs/akero-vue/src/components/navbar/NavbarItem.vue'
import Navbar from '@akerolabs/akero-vue/src/components/navbar/Navbar.vue'
import { accountObj, agencyObj } from '@akerolabs/akero-vue/src/store'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { sleep } from '@akerolabs/akero-vue/src/utils/helpers'
import { MediaManagerProgrammatic } from '@akerolabs/akero-vue/src/components/mediaManager'
import { ModalProgrammatic } from '@akerolabs/akero-vue/src/components/modal'
import PopoutExportModal from './PopoutExportModal.vue'
import { defineComponent, ref, onMounted } from 'vue'
import Preview from './PopoutEditorPreview.vue'
import { defaultOptions } from './helpers.js'

export default defineComponent({
  name: 'AkPopoutEditor',

  components: {
    [SidebarLayout.name]: SidebarLayout,
    [Form.name]: Form,
    [Field.name]: Field,
    [Input.name]: Input,
    [Preview.name]: Preview,
    [Box.name]: Box,
    [Select.name]: Select,
    [Button.name]: Button,
    [NavbarButton.name]: NavbarButton,
    [NavbarItem.name]: NavbarItem,
    [Navbar.name]: Navbar,
    [Icon.name]: Icon,
  },

  setup() {
    const { _id: account } = accountObj()
    const { _id: agency } = agencyObj()
    const forms = ref([])

    const types = ref([
      { label: 'Popout', value: 'popout' },
      { label: 'Modal', value: 'modal' },
    ])

    const locations = ref([
      { label: 'Left', value: 'left' },
      { label: 'Right', value: 'right' },
    ])

    // These are the settings for the popout script, we have set them up this
    // way rather than as an object since we need to only update the config
    // when the inputs are blured, or on the @select event for select menus
    // as the popout script makes HTTP requests to fetch the requested form
    // markup from the API.
    const campaignId = ref(undefined)
    const formId = ref(undefined)
    const bodyHeader = ref(defaultOptions.bodyHeader)
    const bodyText = ref(defaultOptions.bodyText)
    const bodyHeaderColour = ref(defaultOptions.bodyHeaderColour)
    const bodyImg = ref(defaultOptions.bodyImg)
    const heightFromTop = ref(defaultOptions.heightFromTop)
    const akeroPopoutId = ref(defaultOptions.akeroPopoutId)
    const buttonLabel = ref(defaultOptions.buttonLabel)
    const buttonColour = ref(defaultOptions.buttonColour)
    const buttonFontColour = ref(defaultOptions.buttonFontColour)
    const popoutPosition = ref(defaultOptions.popoutPosition)
    const defaultHeight = ref(defaultOptions.defaultHeight)
    const modalTrigger = ref(defaultOptions.modalTrigger)
    const modalTriggerDelay = ref(defaultOptions.modalTriggerDelay)
    const type = ref(types.value.find((i) => i.value === defaultOptions.type))
    const popoutLocation = ref(
      locations.value.find((i) => i.value === defaultOptions.popoutLocation),
    )

    // This will be the config object we bind to the preview window. this will
    // have all the above variables as properties.
    const config = ref({})

    const getForms = async () => {
      const { data: res } = await request.get('/apps', {
        params: {
          type: 'embed',
          per_page: 0,
          account,
          agency,
        },
      })

      forms.value = res.data
    }

    const changeImage = () => {
      MediaManagerProgrammatic.open({
        async callback({ items }) {
          const [image] = items
          const { source } = image
          bodyImg.value = source
          await updateConfig()
        },
      })
    }

    const updateConfig = async () => {
      // need to wait a small ammount to ensure that the refs have
      // all updated before we update the main config object
      await sleep(10)

      config.value = {
        type: type.value.value,
        bodyHeader: bodyHeader.value,
        bodyText: bodyText.value,
        bodyHeaderColour: bodyHeaderColour.value,
        bodyImg: bodyImg.value,
        campaignId: formId.value && formId.value.campaign,
        formId: formId.value && formId.value._id,
        heightFromTop: heightFromTop.value,
        akeroPopoutId: akeroPopoutId.value,
        buttonLabel: buttonLabel.value,
        buttonColour: buttonColour.value,
        buttonFontColour: buttonFontColour.value,
        popoutPosition: popoutPosition.value,
        popoutLocation: popoutLocation.value.value,
        defaultHeight: defaultHeight.value,
        modalTrigger: modalTrigger.value,
        modalTriggerDelay: modalTriggerDelay.value,
        preventAllTracking: true,
        preview: true,
        debug: true,
        env, // defined in the main admin pug file
      }
    }

    const exportPopout = () => {
      ModalProgrammatic.open({
        component: PopoutExportModal,
        size: 'large',
        props: {
          modelValue: config.value,
        },
      })
    }

    onMounted(async () => {
      await getForms()
    })

    return {
      locations,
      config,
      forms,
      types,
      type,
      bodyHeader,
      bodyText,
      bodyHeaderColour,
      bodyImg,
      campaignId,
      formId,
      heightFromTop,
      akeroPopoutId,
      buttonLabel,
      buttonColour,
      buttonFontColour,
      popoutPosition,
      popoutLocation,
      defaultHeight,
      modalTrigger,
      modalTriggerDelay,
      updateConfig,
      changeImage,
      exportPopout,
    }
  },
})
</script>

<i18n>
{
  "en": {
    "Colour": "Colour"
  },
  "en-US": {
    "Colour": "Color"
  }
}
</i18n>
